import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL } from './schema';

const SITE_NAME = 'Fedigma';
const DEFAULT_TITLE = `${SITE_NAME} | Website, ERP & Digital Marketing for Schools and Institutes`;

const setMeta = (selector, attribute, key, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

// Sets the document title, description, canonical URL and social preview tags.
// Pass `null` for the default site title; `undefined` (e.g. data not found) leaves
// the tags to whichever component renders instead, such as NotFound.
// Options: { noindex, type } where type is the Open Graph type (default "website").
const usePageMeta = (title, description, { noindex = false, type = 'website' } = {}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (title === undefined) return;
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const path = pathname !== '/' ? pathname.replace(/\/+$/, '') : '';
    const url = `${SITE_URL}${path}`;

    document.title = fullTitle;
    setCanonical(url);
    setMeta('meta[name="robots"]', 'name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[property="og:type"]', 'property', 'og:type', type);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);

    if (description) {
      setMeta('meta[name="description"]', 'name', 'description', description);
      setMeta('meta[property="og:description"]', 'property', 'og:description', description);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
  }, [title, description, noindex, type, pathname]);
};

export default usePageMeta;
